import { FC, Fragment, useState } from "react";
import { Avatar, Box, Tooltip, Typography } from "@mui/material";
import { CommonStyle } from "Components";
import ImgPath from "Constant/Imgs";
import { SideModalProps } from "DataTypes/Modal";
import { PatientInitialStateType, UserInitialStateType } from "DataTypes/Redux";
import { useDispatch, useSelector } from "react-redux";
import PatientFormatter from "Utils/Patient.Formatter";
import DateTime from "Utils/DateTime";
import EditProfile from "./EditProfile";
import CstmBtn from "Components/CstmBtn";
import useEhrSync from "Hooks/useEhrSync";
import EhrLastSync from "Components/EhrLastSync";
import useEhrRefresh from "Hooks/useEhrRefresh";
import { EhrDomains } from "Constant/Ehr";
import { label } from "Constant/En";
import refreshPatientInfo from "Utils/Ehr";

const ProfileModal: FC<SideModalProps> = (props) => {
  const dispatch = useDispatch();
  const { onClose } = props;
  const { patientInfo, patientOrg, ehr }: PatientInitialStateType = useSelector(
    (state: any) => state?.patient,
  );
  const { streetAddress, address2, city, state, zip, country } = patientInfo;
  const { myInfo }: UserInitialStateType = useSelector(
    (state: any) => state?.user,
  );
  const fullAddress =
    (streetAddress ? `${streetAddress}, ` : "") +
    (address2 ? `${address2}, ` : "") +
    (city ? `${city}, ` : "") +
    (state ? `${state} ` : "") +
    (zip ? `${zip}` : "") +
    ((state || zip) && country ? ", " : "") +
    (country ? country : "");
  const [showLayer2, setShowLayer2] = useState(false);
  const crudPermission = myInfo.roleId != 6 && ehr.vendor === null;
  const agegender =
    (patientInfo.age ? `${patientInfo.age}Y` : "") +
    (patientInfo.gender ? `, ${patientInfo.gender}` : "");
  // Two buttons: a targeted demographics pull where the vendor offers one
  // (eCW does, Athena does not), and a full sync that is always available.
  const demographicsSync = useEhrSync(EhrDomains.DEMOGRAPHICS);
  const allSync = useEhrSync(EhrDomains.ALL);
  // Registered once, on DEMOGRAPHICS rather than ALL: a listener fires on its
  // own domain or on "all", so this catches both syncs. Registering on ALL
  // would miss the demographics one, and registering twice would reload twice.
  useEhrRefresh(EhrDomains.DEMOGRAPHICS, () =>
    refreshPatientInfo(dispatch, patientInfo),
  );

  const handleCloseLayer2: () => void = () => {
    setShowLayer2(false);
  };

  const ProfileData = [
    {
      id: "name",
      label: `${patientInfo.name} ${agegender ? `(${agegender})` : ""}`,
      icon: ImgPath.PersonIcon,
    },
    {
      id: "dob",
      label: DateTime.ToLocalDate(patientInfo.dob),
      icon: ImgPath.CalendarIcon,
    },
    {
      id: "lang",
      label: patientInfo.language,
      icon: ImgPath.LanguageIcon,
    },
    {
      id: "add1",
      label: fullAddress,
      icon: ImgPath.LocationIcon,
    },
    {
      id: "homeno.",
      label: PatientFormatter.MobileNumber(patientInfo.home),
      icon: ImgPath.CallIcon,
    },
    {
      id: "mobno.",
      label: PatientFormatter.MobileNumber(patientInfo.mobile),
      icon: ImgPath.SmartphoneIcon,
    },
    {
      id: "mail",
      label: patientInfo.email,
      icon: ImgPath.EmailIcon
    },
    {
      id: "ehrId",
      label: `EHR ID: ${patientInfo.ehrPatientId}`,
      icon: ImgPath.EhrIcon
    }
  ];

  const OrgData = [
    {
      id: "org-name",
      label: patientOrg.practiceName,
      icon: ImgPath.OrganisationIcon,
    },
    {
      id: "loc-name",
      label: patientOrg.practiceLocationName,
      icon: ImgPath.LocationIcon,
    },
    {
      id: "timezone",
      label: patientOrg.practiceLocationTimezone,
      icon: ImgPath.AddTimeLogIcon,
    },
  ];

  const ProfileUI = (
    <Fragment>
      {/* <Box width={80} height={80} sx={{ ...CommonStyle.sxRowCenter }}>
        <Avatar src={""} alt="" sx={{ height: "100%", width: "100%" }} />
      </Box> */}
      {ProfileData.map((el) => {
        return (
          <Box key={el.id}>
            <el.icon
              color="secondary"
              fontSize="small"
              sx={{ mr: 1, alignSelf: "flex-start" }}
            />
            <Typography
              variant={el.id == "name" ? "body2" : "subtitle1"}
              sx={{ wordBreak: "break-all" }}
            >
              {el.label}
            </Typography>
          </Box>
        );
      })}
    </Fragment>
  );

  const OrgUI = (
    <Fragment>
      <Typography variant="body2" sx={{ ...sxOrghead }}>
        Organization Details
      </Typography>
      {OrgData.map((el) => {
        return (
          <Box key={el.id}>
            <el.icon color="secondary" fontSize="small" />
            <Typography variant="subtitle1" sx={{ ml: 1 }}>
              {el.label}
            </Typography>
          </Box>
        );
      })}
    </Fragment>
  );

  const Layer1 = (
    <Fragment>
      <Box sx={{ ...sxHeader, ...CommonStyle.sxRow }}>
        <Typography fontWeight="bold">Profile</Typography>
        {/* Every control keeps one line of its own, with the sync stamp on a
            second line beneath it. Stacking the stamp under the button alone
            would make that column taller than its neighbours and drop the
            icons out of line with it. */}
        <Box
          sx={{
            ...CommonStyle.sxFlexDirectionColumn,
            alignItems: "flex-end",
          }}
        >
          <Box sx={{ ...CommonStyle.sxRow }}>
            {allSync.canSync && (
              <Tooltip arrow placement="top" title={allSync.title}>
                <span>
                  <CstmBtn
                    label={label.syncFromEhr}
                    btnType="primary"
                    loading={allSync.syncing}
                    loadingText="Syncing..."
                    onClick={allSync.handleSync}
                    sxProps={{ mr: 1, py: 0.4, minWidth: "auto", px: 1.2 }}
                  />
                </span>
              </Tooltip>
            )}
            {crudPermission && (
              <Box
                sx={{ ...CommonStyle.sxRoundIconsMainSm, mr: 1 }}
                onClick={() => setShowLayer2(true)}
              >
                <ImgPath.EditOutlined titleAccess="Edit Profile" />
              </Box>
            )}
            <Box
              sx={{ ...CommonStyle.sxRoundIconsMainSm }}
              onClick={() => {
                handleCloseLayer2();
                onClose();
              }}
            >
              <ImgPath.CloseIcon titleAccess="Close" />
            </Box>
          </Box>
          <EhrLastSync sync={demographicsSync} />
        </Box>
      </Box>
      <Box sx={{ ...sxMain, "& >div": { ...CommonStyle.sxRow, mb: 1 } }}>
        {ProfileUI}
        {OrgUI}
      </Box>
    </Fragment>
  );

  return (
    <Fragment>
      {/* A quarter of the modal is roomy on a wide screen but squeezes the
          email and address into a horizontal scroll on a narrow one, so the
          column keeps a pixel floor and the edit form takes what is left. */}
      <Box
        sx={{
          ...CommonStyle.sxFlexDirectionColumn,
          width: "25%",
          minWidth: 320,
          flexShrink: 0,
          overflow: "hidden",
        }}
      >
        {Layer1}
      </Box>
      <Box width="1%"></Box>
      {crudPermission && showLayer2 && (
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <EditProfile onClose={onClose} />
        </Box>
      )}
    </Fragment>
  );
};
export default ProfileModal;

// The header sizes to its own content rather than to a percentage of the
// modal: with the sync stamp on a second line it needs more than the 8% a
// short viewport gives it, and the overflow used to land on the profile list.
// The body then takes whatever height is left over.
const sxHeader = {
  justifyContent: "space-between",
  flexShrink: 0,
  py: 1,
  px: 2,
};

const sxMain = {
  flex: 1,
  minHeight: 0,
  overflow: "auto",
  overflowX: "hidden",
  px: 2,
  wordBreak: "break-word",
};

const sxOrghead = {
  borderTop: "1px solid",
  borderColor: "custom.main",
  pt: 1,
  my: 1,
  fontWeight: "bold",
};
